import { sortBy } from 'lodash';
import {
  PrejudiceFormCalendrierDepenseCapitalisation,
  PrejudiceFormCalendrierDepenseCapitalisationRow,
} from '../../../types/prejudice.type';
import { Procedure } from '../../../types/procedure.type';
import { Victime } from '../../../types/victime.type';
import { prejudiceBaseDefaultValues } from '../defaultValues';
import { defaultTableDepenseRow } from './FormCalendrierDepense.defaultValues';

export type CalendrierDepenseCapitalisationExistingRow =
  Partial<PrejudiceFormCalendrierDepenseCapitalisationRow>;
export type CalendrierDepenseCapitalisationExistingValues = Omit<
  Partial<PrejudiceFormCalendrierDepenseCapitalisation>,
  'rows' | 'capitalisation' | 'capitalisationTiersPayeurs'
> & {
  rows?: CalendrierDepenseCapitalisationExistingRow[];
  capitalisation?: Partial<
    PrejudiceFormCalendrierDepenseCapitalisation['capitalisation']
  >;
  capitalisationTiersPayeurs?: Partial<
    PrejudiceFormCalendrierDepenseCapitalisation['capitalisationTiersPayeurs']
  >;
};

export const calendrierDepenseCapitalisationDefaultValues = ({
  procedure,
  values,
  victime,
  isExtractedValues,
}: {
  procedure: Procedure;
  values?: CalendrierDepenseCapitalisationExistingValues;
  victime: Partial<Victime>;
  isExtractedValues: boolean;
}): PrejudiceFormCalendrierDepenseCapitalisation => ({
  ...prejudiceBaseDefaultValues({
    values,
  }),
  notes: values?.notes || '',
  rows:
    sortBy(values?.rows || [], (row) => row?.dateDebut)?.map((row) => ({
      ...defaultTableDepenseRow({
        tiersPayeurs: procedure.tiersPayeurs,
        type: row.type || 'ponctuelle',
        isCalculCapitalisation: false,
        procedure,
        victime,
      }),
      ...row,
      priseEnChargeTiersPayeurs: procedure.tiersPayeurs.map((tiersPayeur) => ({
        tiersPayeur,
        montant:
          (Array.isArray(row.priseEnChargeTiersPayeurs)
            ? row.priseEnChargeTiersPayeurs
            : []
          )?.find(
            (priseEnChargeTiersPayeur) =>
              priseEnChargeTiersPayeur.tiersPayeur === tiersPayeur,
          )?.montant || 0,
      })),
      priseEnChargeTiersPayeursProratises: procedure.tiersPayeurs.map(
        (tiersPayeur) => ({
          tiersPayeur,
          montant:
            (Array.isArray(row.priseEnChargeTiersPayeursProratises)
              ? row.priseEnChargeTiersPayeursProratises
              : []
            ).find(
              (priseEnChargeTiersPayeur) =>
                priseEnChargeTiersPayeur.tiersPayeur === tiersPayeur,
            )?.montant || 0,
        }),
      ),
      capitalisation: {
        isLastArrerageViager: row.capitalisation?.isLastArrerageViager || false,
        ageDernierArrerage: row.capitalisation?.ageDernierArrerage || null,
        coefficient: row.capitalisation?.coefficient || 0,
        montantCapitalise: row.capitalisation?.montantCapitalise || 0,
        dateAttribution:
          row.capitalisation?.dateAttribution || row.dateDebut || null,
        dateAttributionType:
          row.capitalisation?.dateAttributionType || 'premier_renouvellement',
      },
      capitalisationTiersPayeurs: {
        parTiersPayeur: procedure.tiersPayeurs.map((tiersPayeur) => ({
          tiersPayeur,
          sommeACapitaliser: 0,
          coefficient: 0,
          montantCapitalise: 0,
          ...row.capitalisationTiersPayeurs?.parTiersPayeur?.find(
            (row) => row.tiersPayeur === tiersPayeur,
          ),
        })),
      },
    })) || [],
  capitalisation: {
    bareme:
      (values && !isExtractedValues
        ? values?.capitalisation?.bareme
        : procedure.baremes?.baremeCapitalisationId) ?? '',
    montantCapitalise: values?.capitalisation?.montantCapitalise || 0,
    enableCapitalisationDifferee:
      values?.capitalisation?.enableCapitalisationDifferee ?? false,
    dateAttributionType:
      values?.capitalisation?.dateAttributionType || 'premier_renouvellement',
  },
  capitalisationTiersPayeurs: {
    parTiersPayeur: procedure.tiersPayeurs.map((tiersPayeur) => {
      const tiersPayeurValues =
        values?.capitalisationTiersPayeurs?.parTiersPayeur?.find(
          (row) => row.tiersPayeur === tiersPayeur,
        );
      return {
        tiersPayeur,
        montantCapitalise: 0,
        ...tiersPayeurValues,
      };
    }),
    montantCapitalise: 0,
  },
  revalorisationCoefficientsType:
    values?.revalorisationCoefficientsType || 'annuel',
  isRentesOption: values?.isRentesOption || false,
  rentes: {
    montant: values?.rentes?.montant || 0,
  },
});
